import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// THE PARTNER'S OWN ACTION ITEMS, MIRRORED ONTO THE STAFF DRAWER.
//
// 🚨 IT RENDERS WHAT THE SERVER SENT. getPartnerTaskState is the single source
// the partner's dashboard and Eva both read, and this is a third surface onto
// the same answer — not a third answer. Nothing is recomputed here: no "if the
// agreement is signed then…", no local idea of which items block a payment.
//
// ⚠️ FOUR STATES NEED FOUR SENTENCES, and counting tasks gives one. An archived
// partner has no items because their account is retired; an unset partner type
// means the list cannot be computed at all; license_pending means they have
// finished and Doorly has not. Rendering "nothing outstanding" for any of those
// would be the staff-side version of telling a partner they are all set.
//
// 🔑 AND IT CHANGES NOTHING ABOUT WHAT THE PARTNER SEES.
// ─────────────────────────────────────────────────────────────────────────────

const FLAG_TONE = {
  red: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' },
  amber: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e' },
};

/** The sentence for each state the server can report. */
const EMPTY_COPY = {
  // ⚠️ THE SERVER SENDS A STAFF SENTENCE FOR THIS ONE. The fallback exists only
  // for a payload that predates it — and it must not read as "all clear", which
  // is what EMPTY_COPY.tasks would have said for a partner who cannot submit.
  license_pending: 'Nothing is outstanding on their side. License verification is still '
    + 'open — see the panel below.',
  archived: 'This account is retired, so there are no outstanding items. That is not the '
    + 'same as finished.',
  blocked_unset_type: 'Their outstanding items cannot be worked out until the partner type is '
    + 'set. This list is blocked, not empty.',
  no_tasks_for_type: 'This partner type has no agreement and no banking setup, so there is '
    + 'genuinely nothing outstanding.',
  tasks: 'Nothing outstanding. Every setup item is complete.',
};

export default function PartnerOverviewState({ partnerId, token, onChanged }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [recomputeMsg, setRecomputeMsg] = useState('');
  // 🔑 P7 (Jesse 2026-11-01): link a partner with no firm to an existing firm. Shown only when the server says the
  // caller may (rd.can_link_firm — Hannah, Jesse or this partner's Partner Success owner); audit-logged server-side.
  const [firms, setFirms] = useState(null);
  const [firmId, setFirmId] = useState('');
  const [firmMsg, setFirmMsg] = useState('');

  const load = useCallback(() => {
    if (!partnerId || !token) return;
    fetch(`${API}/api/admin/partners/${partnerId}/overview-state`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((x) => { if (x.error) setError(x.error); else { setD(x); setError(''); } })
      .catch(() => setError('Could not load this partner’s outstanding items.'));
  }, [partnerId, token]);

  useEffect(load, [load]);

  if (error) {
    return <div style={{ fontSize: 12.5, color: '#b91c1c', padding: '10px 0' }}>{error}</div>;
  }
  if (!d) return null;

  const items = d.action_items || [];
  const blocking = (d.payout && d.payout.blocking) || [];
  const rd = d.readiness || null;

  const recompute = async () => {
    setBusy(true); setRecomputeMsg('');
    try {
      const r = await fetch(`${API}/api/admin/partners/${partnerId}/readiness/recompute`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const x = await r.json();
      if (!r.ok) setRecomputeMsg(x.error || 'Recompute failed.');
      else {
        setRecomputeMsg(x.changed ? `Status changed: ${x.from} \u2192 ${x.to}.`
          : `No change: the status is already ${x.from}.`);
        if (onChanged) onChanged(x);
      }
      load();
    } catch (_) {
      setRecomputeMsg('Recompute failed.');
    } finally { setBusy(false); }
  };

  const loadFirms = () => {
    if (firms) return;
    fetch(`${API}/api/admin/partners/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((x) => setFirms((x.accounts || []).map((a) => ({ id: a.id, name: a.company_name || `Firm #${a.id}` }))))
      .catch(() => setFirmMsg('Could not load the firms.'));
  };
  const linkFirm = async () => {
    if (!firmId) return;
    setBusy(true); setFirmMsg('');
    try {
      const r = await fetch(`${API}/api/admin/partners/${partnerId}/firm-link`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: Number(firmId) }) });
      const x = await r.json();
      if (!r.ok) setFirmMsg(x.error || 'Could not link the firm.');
      else { setFirmMsg(`Linked to ${x.company_name || 'the firm'}.`); if (onChanged) onChanged(x); }
      load();
    } catch (_) {
      setFirmMsg('Could not link the firm.');
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* ── 🚨 FLAGS FIRST, because they change how everything below reads ── */}
      {(d.flags || []).length > 0 && (
        <div style={{ marginTop: 14, display: 'grid', gap: 7 }}>
          {d.flags.map((f) => {
            const t = FLAG_TONE[f.tone] || FLAG_TONE.amber;
            return (
              <div key={f.key} style={{ background: t.bg, border: `1px solid ${t.border}`,
                borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.3,
                  textTransform: 'uppercase', color: t.fg }}>{f.label}</span>
                <div style={{ fontSize: 12.5, color: t.fg, marginTop: 2, lineHeight: 1.5 }}>
                  {f.detail}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── 🔑 SETUP CHECKLIST: THE PARTNER'S OWN LIST (push C) ─────────────
          utils/partnerReadiness is what every partner gate refuses with and
          what the partner's continue screen lists, so this is the same list,
          not a third one. Each row says whose move it is and what staff can
          do. Recompute writes only what the rule computes (audit-logged). */}
      {rd && (
        <div data-testid="partner-readiness" style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#111827' }}>
              Setup checklist <span style={{ fontWeight: 500, color: '#6b7280' }}>(what the partner sees)</span>
            </h4>
            <button type="button" data-testid="partner-readiness-recompute" disabled={busy} onClick={recompute}
              style={{ padding: '4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 6,
                border: '1px solid #3a6ad9', background: '#fff', color: '#3a6ad9',
                cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Recomputing\u2026' : 'Recompute'}
            </button>
          </div>
          <div data-testid="partner-readiness-summary" style={{ fontSize: 12.5, color: '#374151', margin: '6px 0 8px', lineHeight: 1.6 }}>
            {/* 🔑 THE FIRST OUTSTANDING ITEM, NOT THE STORED STATUS (Jesse 2026-11-01): training_pending also
                means "agreement outstanding", so the stored word misled. */}
            <span data-testid="partner-status-label" data-key={rd.status_label?.key || ''}>
              Status: <strong>{rd.status_label?.label || (rd.ready ? 'Active' : 'Not active yet')}</strong>.
            </span>
            {rd.ready ? ' Every activation requirement is met.' : ''}
            {rd.can_submit && (
              <> {' '}{rd.can_submit.action === 'application' ? 'Submit an Application' : 'Send a buyer'}:{' '}
                <strong>{rd.can_submit.ready ? 'allowed' : 'refused'}</strong>.</>
            )}
            {rd.stale && (
              <div style={{ color: '#92400e' }}>
                Stored status is {rd.stored_status}; the rule says {rd.rule_status}. Recompute fixes it.
              </div>
            )}
          </div>
          {(rd.checklist || []).length > 0 ? (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {rd.checklist.map((c) => (
                <li key={c.key} data-testid={`partner-readiness-item-${c.key}`}
                  style={{ padding: '7px 0', borderTop: '1px solid #f3f4f6', fontSize: 12.5, color: '#374151', lineHeight: 1.55 }}>
                  <span style={{ fontWeight: 700, color: '#111827' }}>{c.label}</span>
                  <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                    background: c.who === 'partner' ? '#eff6ff' : '#fffbeb',
                    color: c.who === 'partner' ? '#1e40af' : '#92400e' }}>
                    {c.who === 'partner' ? 'Partner' : 'Doorly'}
                  </span>
                  {c.since && (
                    <span style={{ marginLeft: 6, color: '#6b7280' }}>
                      since {new Date(c.since).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}
                    </span>
                  )}
                  {c.staff_action && <div style={{ color: '#6b7280' }}>{c.staff_action}</div>}
                  {c.control === 'firm_link' && (
                    <div data-testid="partner-firm-link" style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <select data-testid="partner-firm-link-select" aria-label="Firm" value={firmId} onFocus={loadFirms} onClick={loadFirms}
                        onChange={(e) => setFirmId(e.target.value)}
                        style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #d1d5db', minWidth: 200 }}>
                        <option value="">{firms ? 'Choose a firm' : 'Choose a firm…'}</option>
                        {(firms || []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                      <button type="button" data-testid="partner-firm-link-button" disabled={busy || !firmId} onClick={linkFirm}
                        style={{ padding: '4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 6,
                          border: '1px solid #3a6ad9', background: '#fff', color: '#3a6ad9',
                          cursor: busy || !firmId ? 'default' : 'pointer' }}>
                        Link to a firm
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ fontSize: 12.5, color: '#166534' }}>Nothing left for activation.</div>
          )}
          {firmMsg && (
            <div data-testid="partner-firm-link-msg" style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>{firmMsg}</div>
          )}
          {recomputeMsg && (
            <div data-testid="partner-readiness-recompute-msg" style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>{recomputeMsg}</div>
          )}
        </div>
      )}

      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 800, color: '#111827' }}>
          Action items
        </h4>
        {/* ⚠️ THE SAME ROWS THE PARTNER SEES UNDER "MY TASKS", with the same
            wording — so a rep reading one out loud is reading what is on the
            partner's screen. */}
        {items.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#374151',
            lineHeight: 1.75 }}>
            {items.map((t) => (
              <li key={t.key}>
                {t.label}
                {/* 🔑 WHERE, ONLY WHEN THE SERVER SENT ONE. The counter-signature
                    is the first item that can have nowhere to send anybody. */}
                {t.where ? <span style={{ color: '#9ca3af' }}> · {t.where}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ fontSize: 12.5, color: d.task_state === 'tasks' ? '#166534' : '#92400e',
            lineHeight: 1.55 }}>
            {d.notice || EMPTY_COPY[d.task_state] || EMPTY_COPY.tasks}
          </div>
        )}

        {/* ── 🚨 PAYOUT READINESS IS ITS OWN LINE AND ITS OWN LIST ──────────
            Outstanding is not the same as payout-blocking. The task list above
            covers profile, license, W-9 and training; payment is gated on the
            agreement, its counter-signature and banking. One line conflating
            them is how a rep tells a partner their W-9 is holding up a payment
            that it is not holding up. */}
        <div style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.6,
          color: blocking.length ? '#92400e' : '#166534' }}>
          <strong>Payout readiness: </strong>
          {blocking.length === 0
            ? (d.payout && d.payout.ready
              ? 'ready — nothing on this partner’s account is holding a payment.'
              : 'cannot be determined until the partner type is set.')
            : `held — needs ${blocking.map((b) => b.label).join(' and ')}.`}
        </div>
      </div>
    </>
  );
}
